import { EntitiesMap, EffectiveDateRecord } from "@/models.ts";

export function ISODateStringToYYYYMMDD(d: string | undefined): string {
  return d === undefined ? "" : d.replaceAll("-", "");
}

export function dateToISOString(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toJSON()
    .slice(0, 10);
}
export function arrayAsMap<KeyType, ValueType>(
  records: ValueType[],
  keyFn: (o: ValueType) => KeyType
): Map<KeyType, ValueType> {
  var map = new Map<KeyType, ValueType>();
  records.forEach((x) => {
    const key = keyFn(x);
    map.set(key, x);
  });
  return map;
}

export function makeMap<KeyType, ValueType>(
  records: ValueType[],
  keyFn: (o: ValueType) => KeyType,
  compareFn: (x: ValueType, y: ValueType) => number
): Map<KeyType, ValueType[]> {
  var map = new Map<KeyType, ValueType[]>();
  records.forEach((x) => {
    const key = keyFn(x);
    if (!map.has(key)) {
      map.set(key, [x]);
    } else {
      map.get(key)?.push(x);
    }
  });
  map.forEach((value) => {
    value.sort(compareFn);
  });
  return map;
}

// Groups 'records' using the provided 'keyFn' and then sorts the elements in each group with the optionally provided 'compareFn'
export function makeRecord<ValueType>(
  records: ValueType[],
  keyFn: (o: ValueType) => string,
  compareFn?: (x: ValueType, y: ValueType) => number
): Record<string, ValueType[]> {
  var map: Record<string, ValueType[]> = {};
  records.forEach((x) => {
    const key = keyFn(x);
    if (key in map === false) {
      map[key] = [x];
    } else {
      map[key].push(x);
    }
  });
  if (compareFn) {
    Object.values(map).forEach((x) => {
      x.sort(compareFn);
    });
  }
  return map;
}

export function groupEntities<RecordType>(
  keyFn: (o: RecordType) => string,
  records: (RecordType & EffectiveDateRecord)[]
): EntitiesMap<RecordType> {
  const map = makeRecord<RecordType & EffectiveDateRecord>(
    records,
    keyFn,
    (x, y) => y.EffectiveStartDate.localeCompare(x.EffectiveStartDate)
  );
  const newMap = map as any as EntitiesMap<RecordType>;
  for (const [k, v] of Object.entries(map)) {
    newMap[k] = { records: v };
  }
  return newMap;
}

export function addPlaceholderEntities<RecordType>(
  entities: EntitiesMap<RecordType>,
  references: string[],
  createPlaceholder: (id: string) => RecordType & EffectiveDateRecord
): EntitiesMap<RecordType> {
  references.forEach((r) => {
    if (r in entities === false) {
      entities[r] = { records: [createPlaceholder(r)] };
    }
  });
  return entities;
}

export function normalizeCodeSetValue(
  value: string,
  options: { Value: string }[]
): string {
  if (!value) return value;
  const match = options.find(
    (o) => o.Value.toUpperCase() === value.toUpperCase()
  );
  return match ? match.Value : value;
}

export function caseInsensitiveCompare(a: string, b: string) {
  // Convert both strings to lowercase for case-insensitive comparison
  const lowerA = a.toLowerCase();
  const lowerB = b.toLowerCase();

  // Use localeCompare for robust alphabetical sorting
  return lowerA.localeCompare(lowerB);
}

export function convertMinutesToMilliseconds(minutes: number): number {
  return Math.round(minutes * 60 * 1000);
}

export function validateMinDate(
  value: string | undefined | null
): string | true {
  if (!value) return true;

  const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoRegex.test(value)) return "Invalid date format";

  const [yStr, mStr, dStr] = value.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);

  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return "Invalid date format";
  }

  // Construct a UTC date and verify components to avoid JS parsing quirks/timezone shifts
  const parsed = new Date(Date.UTC(y, m - 1, d));
  if (
    parsed.getUTCFullYear() !== y ||
    parsed.getUTCMonth() + 1 !== m ||
    parsed.getUTCDate() !== d
  ) {
    return "Invalid date format";
  }

  const minDate = new Date(Date.UTC(1900, 0, 1));
  if (parsed.getTime() < minDate.getTime()) {
    return "Invalid year";
  }

  return true;
}
